import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Navigate, Route, Routes } from "react-router";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import Index from "./pages/Index.tsx";
import NotFound from "./pages/NotFound.tsx";
import PrivacyPolicy from "./pages/PrivacyPolicy.tsx";
import Contact from "./pages/Contact.tsx";
import About from "./pages/About.tsx";
import Locations from "./pages/Locations.tsx";
import TermsConditions from "./pages/TermsConditions.tsx";
import HomeMoves from "./pages/services/HomeMoves.tsx";
import ApartmentMoves from "./pages/services/ApartmentMoves.tsx";
import OfficeRelocations from "./pages/services/OfficeRelocations.tsx";
import SingleItemMoves from "./pages/services/SingleItemMoves.tsx";
import SameDayMoves from "./pages/services/SameDayMoves.tsx";
import Melbourne from "./pages/locations/Melbourne.tsx";
import ComingSoonLocation from "./pages/locations/ComingSoon.tsx";
import SuburbPage from "./pages/locations/SuburbPage.tsx";
import { suburbs } from "./content/suburbs.ts";
import Unsubscribe from "./pages/Unsubscribe.tsx";
import WasteServicePage from "./pages/waste/WasteServicePage.tsx";
import { wasteServices } from "./content/wasteServices.tsx";
import Blog from "./pages/Blog.tsx";
import ArticlePage from "./pages/blog/ArticlePage.tsx";
import { blogPosts, legacyBlogSlugRedirects } from "./content/blogPosts.ts";
import HashScrollHandler from "./components/HashScrollHandler.tsx";
import PageViewTracker from "./components/PageViewTracker.tsx";

const comingSoonCities = [
  { city: "Sydney", slug: "sydney" },
  { city: "Brisbane", slug: "brisbane" },
  { city: "Perth", slug: "perth" },
  { city: "Adelaide", slug: "adelaide" },
  { city: "Gold Coast", slug: "gold-coast" },
];

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <HashScrollHandler />
        <PageViewTracker />
        <Routes>
          <Route path="/" element={<Index />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/about" element={<About />} />
          <Route path="/locations" element={<Locations />} />
          <Route path="/terms-conditions" element={<TermsConditions />} />
          <Route path="/home-moves" element={<HomeMoves />} />
          <Route path="/apartment-moves" element={<ApartmentMoves />} />
          <Route path="/office-relocations" element={<OfficeRelocations />} />
          <Route path="/single-item-moves" element={<SingleItemMoves />} />
          <Route path="/same-day-moves" element={<SameDayMoves />} />
          <Route path="/melbourne" element={<Melbourne />} />
          {suburbs.map((s) => (
            <Route key={s.slug} path={`/${s.slug}`} element={<SuburbPage slug={s.slug} />} />
          ))}
          {wasteServices.map((s) => (
            <Route key={s.slug} path={`/${s.slug}`} element={<WasteServicePage slug={s.slug} />} />
          ))}
          <Route path="/blog" element={<Blog />} />
          {blogPosts.map((p) => (
            <Route key={p.slug} path={`/blog/${p.slug}`} element={<ArticlePage slug={p.slug} />} />
          ))}
          {Object.entries(legacyBlogSlugRedirects).map(([oldSlug, newSlug]) => (
            <Route
              key={oldSlug}
              path={`/blog/${oldSlug}`}
              element={<Navigate to={`/blog/${newSlug}`} replace />}
            />
          ))}
          <Route path="/unsubscribe" element={<Unsubscribe />} />
          {comingSoonCities.map((c) => (
            <Route
              key={c.slug}
              path={`/${c.slug}`}
              element={<ComingSoonLocation city={c.city} slug={c.slug} />}
            />
          ))}
          {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
